import { useEffect, useRef, useState, useCallback } from 'react';

// ✅ Auto Logout Component
// Usage: <AutoLogout onLogout={handleLogout} timeoutMinutes={30} />

const TIMEOUT_MINUTES = 30;  // 30 minutes idle = logout
const WARNING_MINUTES = 5;   // Show warning 5 mins before logout

function AutoLogout({ onLogout, timeoutMinutes = TIMEOUT_MINUTES }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown,   setCountdown]   = useState(WARNING_MINUTES * 60);
  const timeoutRef  = useRef(null);
  const warningRef  = useRef(null);
  const countdownRef= useRef(null);

  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  const WARNING_MS = (timeoutMinutes - WARNING_MINUTES) * 60 * 1000;

  // ✅ Logout + clear everything
  const doLogout = useCallback(() => {
    // Clear all timers
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);

    // ✅ Security: Clear all stored data!
    localStorage.removeItem('token');
    sessionStorage.clear();

    // ✅ Clear any cached form data (security!)
    if (document.forms) {
      Array.from(document.forms).forEach(form => form.reset());
    }

    setShowWarning(false);
    if (onLogout) onLogout();
  }, [onLogout]);

  // ✅ Show warning before logout
  const showLogoutWarning = useCallback(() => {
    setShowWarning(true);
    setCountdown(WARNING_MINUTES * 60);

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          doLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [doLogout]);

  // ✅ Reset timer on user activity
  const resetTimer = useCallback(() => {
    if (showWarning) return; // Don't reset if warning showing

    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);

    // Set warning timer
    warningRef.current = setTimeout(showLogoutWarning, WARNING_MS);

    // Set logout timer
    timeoutRef.current = setTimeout(doLogout, TIMEOUT_MS);
  }, [showWarning, doLogout, showLogoutWarning, WARNING_MS, TIMEOUT_MS]);

  // ✅ Track user activity
  useEffect(() => {
    const events = [
      'mousedown', 'mousemove', 'keypress',
      'scroll', 'touchstart', 'click',
    ];

    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer(); // Start timer

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
      clearInterval(countdownRef.current);
    };
  }, [resetTimer]);

  const stayLoggedIn = () => {
    clearInterval(countdownRef.current);
    setShowWarning(false);
    resetTimer();
  };

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;

  if (!showWarning) return null;

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={{fontSize:'48px',marginBottom:'12px'}}>⏰</div>
        <h2 style={{color:'#E74C3C',margin:'0 0 8px'}}>
          Session Timeout Warning!
        </h2>
        <p style={{color:'#555',marginBottom:'16px',fontSize:'14px'}}>
          {timeoutMinutes} minutes-ல் activity இல்லை!
          <br/>
          Auto logout ஆகும்:
        </p>

        {/* Countdown */}
        <div style={S.countdown}>
          <span style={S.countNum}>{String(mins).padStart(2,'0')}</span>
          <span style={S.colon}>:</span>
          <span style={S.countNum}>{String(secs).padStart(2,'0')}</span>
        </div>
        <p style={{color:'#888',fontSize:'12px',marginBottom:'20px'}}>
          minutes : seconds
        </p>

        <div style={{display:'flex',gap:'12px'}}>
          <button style={S.logoutBtn} onClick={doLogout}>
            🚪 Logout Now
          </button>
          <button style={S.stayBtn} onClick={stayLoggedIn}>
            ✅ Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position:'fixed',inset:0,
    background:'rgba(0,0,0,0.7)',
    zIndex:9999,display:'flex',
    alignItems:'center',justifyContent:'center',
    padding:'20px',
  },
  modal: {
    background:'white',borderRadius:'20px',
    padding:'40px',textAlign:'center',
    maxWidth:'360px',width:'100%',
    boxShadow:'0 20px 60px rgba(0,0,0,0.4)',
  },
  countdown: {
    display:'flex',alignItems:'center',
    justifyContent:'center',gap:'4px',
    marginBottom:'4px',
  },
  countNum: {
    background:'#E74C3C',color:'white',
    padding:'12px 20px',borderRadius:'10px',
    fontSize:'32px',fontWeight:'900',
    fontFamily:'monospace',minWidth:'70px',
  },
  colon: {
    fontSize:'32px',fontWeight:'900',
    color:'#E74C3C',
  },
  logoutBtn: {
    flex:1,padding:'12px',borderRadius:'8px',
    border:'1px solid #DDD',background:'white',
    cursor:'pointer',fontWeight:'600',
    color:'#E74C3C',fontSize:'14px',
  },
  stayBtn: {
    flex:2,padding:'12px',borderRadius:'8px',
    border:'none',background:'#27AE60',
    color:'white',cursor:'pointer',
    fontWeight:'700',fontSize:'14px',
  },
};

export default AutoLogout;

